import { addNotesToCollection } from "@/actions/addNotesToCollection";
import { EventContext } from "@/domains/metrics/context";
import { logger } from "@/modules/logger";
import { stringModule } from "@/modules/string";
import { toastModule } from "@/modules/toast";
import { uuidModule } from "@/modules/uuid";
import { AppStore } from "@/store";
import { type INoteObservable } from "@/store/note";
import { TemplateObservable } from "@/store/templates/TemplateObservable";

export const createCollection = async ({
  collectionId,
  title,
  description,
  store,
  notes,
  eventContext,
  showToast = true,
  navigateToCollection,
}: {
  collectionId?: string;
  title: string;
  description?: string;
  notes?: (INoteObservable | TemplateObservable)[];
  store: AppStore;
  eventContext: EventContext;
  showToast?: boolean;
  navigateToCollection?: boolean;
}) => {
  const id = collectionId ?? uuidModule.generate();

  // create the collection
  await store.collections.createCollection({
    collectionId: id,
    title,
    description,
    eventContext,
  });
  const newCollection = await store.collections.getAsync(id);

  // sanity check and ensure newCollection is not undefined
  if (!newCollection) {
    logger.error({
      message: "[createCollection]: Failed to create collection.",
      info: { collectionId: id, title: stringModule.compactTitleForLogging(title) },
    });
    return;
  }

  // make sure the new collection shows up in Recents
  await newCollection.addToRecents();

  // optionally attach notes
  if (notes) {
    await addNotesToCollection({
      notes,
      collection: newCollection,
      store,
      eventContext,
    });
  } else {
    if (showToast) {
      toastModule.triggerToast({
        content: (
          <div>
            <a onClick={() => store.navigation.goToCollection({ collectionId: newCollection.id })}>
              {newCollection.label}
            </a>{" "}
            created
          </div>
        ),
      });
    }
  }

  if (navigateToCollection) {
    store.navigation.goToCollection({ collectionId: newCollection.id });
  }
};
